import { fmt } from "./DashboardPrimitives";
import { useTranslation } from "react-i18next";
import { TbUpload, TbInfoCircle, TbBook2, TbFileEuro, TbRocket, TbChartBar, TbHelpCircle, TbChevronDown, TbCoinEuro, TbFilePlus } from "react-icons/tb";
import { FaUserInjured } from "react-icons/fa6";
import { useId, useState, useRef, useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useEscKey } from "src/hooks/useEscKey";
import { useApi } from "src/hooks/useApi";
import Loader from "src/components/Loader/Loader";

const DUNNING_COLORS = ["#22c55e", "#f59e0b", "#ef4444"];
const DUNNING_LEVELS = [1, 2, 3];


function HeroKpiInfo({ helpText, ariaLabel }: { helpText: string; ariaLabel: string }) {
    const uid = useId().replace(/:/g, "");
    const descId = `hero-kpi-desc-${uid}`;
    const [tipOpen, setTipOpen] = useState(false);
    return (
        <span className="hero-kpi-info">
            <span id={descId} className="hero-kpi-info__sr-only">
                {helpText}
            </span>
            <button
                type="button"
                className="hero-kpi-info__btn"
                aria-describedby={descId}
                aria-label={ariaLabel}
                onMouseEnter={() => setTipOpen(true)}
                onMouseLeave={() => setTipOpen(false)}
                onFocus={() => setTipOpen(true)}
                onBlur={() => setTipOpen(false)}
            >
                <TbInfoCircle aria-hidden />
            </button>
            {tipOpen && (
                <span className="hero-kpi-info__tip" role="tooltip">
                    {helpText}
                </span>
            )}
        </span>
    );
}


interface YearStat { label: string; sent: number; paid: number; }

// ── Inline year bar chart popup ───────────────────────────────────────────────

function niceMax(v: number): number {
    if (v <= 0) return 100;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    const candidates = [1, 2, 2.5, 5, 10].map(f => f * mag);
    return candidates.find(c => c >= v) ?? v;
}

function fmtY(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
    return String(Math.round(n));
}

function YearPopup({
    type,
    data,
    year,
    onClose,
}: {
    type: "sent" | "paid";
    data: YearStat[];
    year: number;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    useEscKey(onClose, true);
    const currentMonth = new Date().getMonth();
    const color = type === "sent" ? "#6366f1" : "#22c55e";

    const values = data.map(d => type === "sent" ? d.sent : d.paid);
    const yMax   = niceMax(Math.max(...values, 1));

    const W = 460, H = 150;
    const PAD = { top: 14, right: 10, bottom: 26, left: 48 };
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const barW  = plotW / 12;
    const gap   = barW * 0.22;
    const GRIDS = 4;

    return (
        <div className="hero-popup-overlay" onClick={onClose}>
            <div className="hero-popup" onClick={e => e.stopPropagation()}>
                <div className="hero-popup__header">
                    <span className="hero-popup__title">
                        {t(type === "sent" ? "dashboard.sent" : "dashboard.paid")} {year}
                    </span>
                    <button className="hero-popup__close" onClick={onClose}>×</button>
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} className="hero-popup__chart">
                    {Array.from({ length: GRIDS + 1 }, (_, i) => {
                        const v = (yMax * i) / GRIDS;
                        const y = PAD.top + (1 - v / yMax) * plotH;
                        return (
                            <g key={i}>
                                <line x1={PAD.left} y1={y} x2={PAD.left + plotW} y2={y} stroke="#f0f0f4" strokeWidth={1} />
                                <text x={PAD.left - 4} y={y} textAnchor="end" dominantBaseline="middle" fontSize={9} fill="#9ca3af">
                                    {fmtY(v)} €
                                </text>
                            </g>
                        );
                    })}
                    {values.map((v, i) => {
                        const bH = (v / yMax) * plotH;
                        const x  = PAD.left + i * barW + gap / 2;
                        const w  = barW - gap;
                        const y  = PAD.top + plotH - bH;
                        return (
                            <g key={i}>
                                <rect x={x} y={y} width={w} height={Math.max(bH, 0)} rx={2} fill={color} opacity={i === currentMonth ? 1 : 0.38} />
                                <text x={x + w / 2} y={PAD.top + plotH + 14} textAnchor="middle" fontSize={8} fill={i === currentMonth ? "#374151" : "#9ca3af"} fontWeight={i === currentMonth ? 600 : 400}>
                                    {data[i].label}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
}

function HeroMiniStat({
    icon,
    label,
    value,
    sub,
    accent,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    value: string;
    sub?: string;
    accent: string;
    onClick: () => void;
}) {
    return (
        <button type="button" className="hero-mini-stat" onClick={onClick}>
            <span className="hero-mini-stat__icon" style={{ background: accent + "22", color: accent }}>
                {icon}
            </span>
            <span className="hero-mini-stat__body">
                <span className="hero-mini-stat__value">{value}</span>
                <span className="hero-mini-stat__label">{label}</span>
                {sub != null && sub !== "" && (
                    <span className="hero-mini-stat__sub">{sub}</span>
                )}
            </span>
        </button>
    );
}

// ── Overdue breakdown popup ───────────────────────────────────────────────────

const LEVEL_KEYS = ["friendly", "demanding", "final", "legal"] as const;
const LEVEL_BADGES = ["M1", "M2", "M3", "Legal"];
const LEVEL_COLORS = ["#f59e0b", "#ef4444", "#dc2626", "#7c3aed"];

function OverduePopup({
    breakdown,
    totalFines,
    total,
    onClose,
}: {
    breakdown: OverdueBreakdownItem[];
    totalFines: number;
    total: number;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    useEscKey(onClose, true);
    const hasAny = breakdown.some((b) => b.count > 0);

    return (
        <div className="hero-popup-overlay" onClick={onClose}>
            <div className="hero-popup hero-popup--overdue" onClick={(e) => e.stopPropagation()}>
                <div className="hero-popup__header">
                    <span className="hero-popup__title">{t("dashboard.overdueBreakdown")}</span>
                    <button className="hero-popup__close" onClick={onClose}>×</button>
                </div>
                {!hasAny ? (
                    <p className="hero-popup__empty">{t("dashboard.overdueNoData")}</p>
                ) : (
                    <table className="overdue-breakdown-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th className="overdue-breakdown-table__num">{t("table.invoices")}</th>
                                <th className="overdue-breakdown-table__amt">{t("dashboard.overdueBaseAmounts")}</th>
                                <th className="overdue-breakdown-table__amt overdue-breakdown-table__fines">{t("dashboard.overdueTotalFines")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {breakdown.map((row, i) => (
                                <tr key={i} className={row.count === 0 ? "overdue-breakdown-table__row--empty" : ""}>
                                    <td>
                                        <span className="overdue-breakdown-table__badge" style={{ background: LEVEL_COLORS[i] + "22", color: LEVEL_COLORS[i] }}>
                                            {LEVEL_BADGES[i]}
                                        </span>
                                        {" "}
                                        {t(`filters.dunningLevels.${LEVEL_KEYS[i]}`)}
                                    </td>
                                    <td className="overdue-breakdown-table__num">{row.count > 0 ? row.count : "—"}</td>
                                    <td className="overdue-breakdown-table__amt">{row.count > 0 ? fmt(row.amount) : "—"}</td>
                                    <td className="overdue-breakdown-table__amt overdue-breakdown-table__fines">{row.fines > 0 ? fmt(row.fines) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {totalFines > 0 && (
                                <tr className="overdue-breakdown-table__fines-row">
                                    <td colSpan={3}>{t("dashboard.overdueTotalFines")}</td>
                                    <td className="overdue-breakdown-table__amt overdue-breakdown-table__fines">{fmt(totalFines)}</td>
                                </tr>
                            )}
                            <tr className="overdue-breakdown-table__total-row">
                                <td colSpan={3}>{t("dashboard.overdue")}</td>
                                <td className="overdue-breakdown-table__amt">{fmt(total)}</td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}

// ── PVS savings history popup ───────────────────────────────────────────────

interface PvsHistoryRow {
    year: number;
    month: number;
    result: { savings: number; pvsCost: number } | null;
}

function PvsHistoryPopup({
    onClose,
    canOpenCharges,
    onOpenCharges,
}: {
    onClose: () => void;
    canOpenCharges: boolean;
    onOpenCharges: () => void;
}) {
    const { t } = useTranslation();
    useEscKey(onClose, true);
    // Only fetches once this popup actually mounts (i.e. once it's opened) — computeChargesPeriod
    // isn't cheap run once, let alone per month, so this must never run on a plain dashboard load.
    const { data: rows, loading, error } = useApi<PvsHistoryRow[]>("/charges/pvs-savings/history?months=6");

    return (
        <div className="hero-popup-overlay" onClick={onClose}>
            <div className="hero-popup" onClick={(e) => e.stopPropagation()}>
                <div className="hero-popup__header">
                    <span className="hero-popup__title">{t("dashboard.pvsHistoryTitle")}</span>
                    <button className="hero-popup__close" onClick={onClose}>×</button>
                </div>
                {loading && <Loader />}
                {error && <p className="hero-popup__empty">{error}</p>}
                {rows && rows.length === 0 && (
                    <p className="hero-popup__empty">{t("dashboard.pvsHistoryEmpty")}</p>
                )}
                {rows && rows.length > 0 && (
                    <table className="pvs-history-table">
                        <tbody>
                            {rows.map((row, i) => {
                                const label = new Date(row.year, row.month - 1, 1)
                                    .toLocaleDateString(undefined, { month: "long", year: "numeric" });
                                const negative = row.result !== null && row.result.savings < 0;
                                return (
                                    <tr key={`${row.year}-${row.month}`} className={i === 0 ? "pvs-history-table__row--current" : undefined}>
                                        <td className="pvs-history-table__month">
                                            {label}
                                            {i === 0 && <span className="pvs-history-table__current-tag">{t("dashboard.pvsHistoryCurrent")}</span>}
                                        </td>
                                        <td className={`pvs-history-table__value${negative ? " pvs-history-table__value--negative" : ""}`}>
                                            {row.result === null
                                                ? <span className="pvs-history-table__none">{t("dashboard.pvsHistoryNotConfigured")}</span>
                                                : negative
                                                    ? t("dashboard.pvsHistoryNegative", { amount: fmt(Math.abs(row.result.savings)) })
                                                    : t("dashboard.pvsHistoryPositive", { amount: fmt(row.result.savings) })}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {canOpenCharges && (
                    <button type="button" className="pvs-history-table__charges-link" onClick={onOpenCharges}>
                        {t("dashboard.pvsHistoryOpenCharges")}
                    </button>
                )}
            </div>
        </div>
    );
}

/** The "you save X € vs. a PVS" pill plus its history popup — shared by the full HeroCard and
 *  the staff dashboard's hero, since staff with permPvsSavings get the stripped staff view.
 *  `canOpenCharges`: Admin only (the Charges page is Admin-only). */
export function PvsSavingsPill({ savings, canOpenCharges, className }: { savings: number; canOpenCharges: boolean; className?: string }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [historyOpen, setHistoryOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                className={`hero-card__pvs${savings >= 0 ? "" : " hero-card__pvs--more"}${className ? ` ${className}` : ""}`}
                onClick={() => setHistoryOpen(true)}
            >
                <TbCoinEuro aria-hidden className="hero-card__pvs-icon" />
                <span className="hero-card__pvs-text">
                    {savings >= 0
                        ? t("dashboard.pvsSavingsPositive", { amount: fmt(savings) })
                        : t("dashboard.pvsSavingsNegative", { amount: fmt(Math.abs(savings)) })}
                </span>
            </button>
            {historyOpen && (
                <PvsHistoryPopup
                    canOpenCharges={canOpenCharges}
                    onOpenCharges={() => { setHistoryOpen(false); navigate("/charges"); }}
                    onClose={() => setHistoryOpen(false)}
                />
            )}
        </>
    );
}

// ── HeroCard ──────────────────────────────────────────────────────────────────

type OverdueBreakdownItem = { count: number; amount: number; fines: number; };

interface HeroCardProps {
    title?: string;
    firstName?: string;
    lastName?: string;
    praxisName?: string;
    totalOutstanding: number;
    /** Invoices not yet sent (same filter as amount). */
    pendingToSendCount: number;
    amountPendingToSend: number;
    amountSent: number;
    amountPaid: number;
    amountOverdue: number;
    overdueBreakdown: OverdueBreakdownItem[];
    overdueTotalFines: number;
    yearMonthlyData: YearStat[];
    canUpload?: boolean;
    onUpload: () => void;
    /** Opens manual invoice creation (/invoices/new) — same permission as uploading. */
    onCreateInvoice?: () => void;
    onOpenFlowStoryboard: () => void;
    onOpenGettingStarted: () => void;
    onOpenStats: () => void;
    quotaCount?: number;
    quotaAllowance?: number;
    quotaOverageRate?: number;
    /** Undefined = not permitted to see it, not yet loaded, or not configured by the praxis —
     *  the line is omitted for all three, same convention as quotaCount/quotaAllowance above. */
    pvsSavings?: number;
    /** True only for an Admin. The pill always opens the history popup regardless (that page
     *  needs no Charges access, just the same permPvsSavings grant) — this only controls
     *  whether the popup also offers a link to the full Charges page, which stays Admin-only. */
    canOpenPvsSavings?: boolean;
    showOverviewSections?: boolean;
    patientCount: number;
    totalInvoiceCount: number;
    openInvoiceCount: number;
    dunningCounts: number[];
    dunningAmounts: number[];
}

export function HeroCard({
    title,
    firstName,
    lastName,
    praxisName,
    totalOutstanding,
    pendingToSendCount,
    amountPendingToSend,
    amountSent,
    amountPaid,
    amountOverdue,
    overdueBreakdown,
    overdueTotalFines,
    yearMonthlyData,
    canUpload = true,
    onUpload,
    onCreateInvoice,
    onOpenFlowStoryboard,
    onOpenGettingStarted,
    onOpenStats,
    quotaCount,
    quotaAllowance,
    quotaOverageRate,
    pvsSavings,
    canOpenPvsSavings = false,
    showOverviewSections = true,
    patientCount,
    totalInvoiceCount,
    openInvoiceCount,
    dunningCounts,
    dunningAmounts,
}: HeroCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showQuotaTip, setShowQuotaTip] = useState(false);
    const [popup, setPopup] = useState<"sent" | "paid" | "overdue" | null>(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const helpRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!helpOpen) return;
        const handler = (e: MouseEvent) => {
            if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
                setHelpOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [helpOpen]);
    const year = new Date().getFullYear();
    const dunningLabels = [
        t("dashboard.friendly"),
        t("dashboard.demanding"),
        t("dashboard.final"),
    ];
    const displayCounts = dunningCounts.slice(1, 4);
    const displayAmounts = dunningAmounts.slice(1, 4);

    return (
        <>
            <div className="hero-card">
                <div className="hero-card__main">
                <div className="hero-card__left">
                    <div className="hero-card__left-core">
                    <div className="hero-card__greeting">{t("dashboard.welcomeBack")}</div>
                    <div className="hero-card__name">
                        {title && <span className="hero-card__title">{title} </span>}{firstName} {lastName}
                    </div>
                    <div className="hero-card__praxis">{praxisName}</div>
                    {quotaAllowance != null && quotaCount != null && (
                        <div
                            className="hero-card__quota"
                            onMouseEnter={() => setShowQuotaTip(true)}
                            onMouseLeave={() => setShowQuotaTip(false)}
                        >
                            <div className="hero-card__quota-header">
                                <span className="hero-card__quota-label">
                                    {t("dashboard.invoicesThisMonth")}
                                </span>
                                <span className="hero-card__quota-count">
                                    {quotaCount}/{quotaAllowance}
                                </span>
                            </div>
                            <div className="hero-card__quota-track">
                                <div
                                    className="hero-card__quota-fill"
                                    style={{ transform: `scaleX(${Math.min(1, quotaCount / quotaAllowance)})` }}
                                />
                            </div>
                            {showQuotaTip && (
                                <div className="hero-card__quota-tip">
                                    {t("dashboard.quotaTip", {
                                        count: quotaCount,
                                        allowance: quotaAllowance,
                                        rate: quotaOverageRate != null ? quotaOverageRate.toFixed(2) : "0",
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {pvsSavings != null && (
                        <PvsSavingsPill savings={pvsSavings} canOpenCharges={canOpenPvsSavings} />
                    )}
                    <div className="hero-card__total-head">
                        <div className="hero-card__total-label">{t("dashboard.totalOutstanding")}</div>
                        <HeroKpiInfo
                            helpText={t("dashboard.kpiHelp.outstanding")}
                            ariaLabel={t("dashboard.kpiHelp.moreAbout", { topic: t("dashboard.totalOutstanding") })}
                        />
                    </div>
                    <div className="hero-card__total-value">
                        {fmt(totalOutstanding)}
                    </div>
                    <div className="hero-card__stats">
                        <div className="hero-stat-wrap">
                            <button
                                type="button"
                                className="hero-stat hero-stat--clickable"
                                onClick={() => setPopup("sent")}
                            >
                                <span className="hero-stat__label">{t("dashboard.sent")}</span>
                                <span className="hero-stat__value">{fmt(amountSent)}</span>
                            </button>
                            <div className="hero-stat-wrap__help">
                                <HeroKpiInfo
                                    helpText={t("dashboard.kpiHelp.sent")}
                                    ariaLabel={t("dashboard.kpiHelp.moreAbout", { topic: t("dashboard.sent") })}
                                />
                            </div>
                        </div>
                        <div className="hero-stat-wrap">
                            <button
                                type="button"
                                className="hero-stat hero-stat--success hero-stat--clickable"
                                onClick={() => setPopup("paid")}
                            >
                                <span className="hero-stat__label">{t("dashboard.paid")}</span>
                                <span className="hero-stat__value">{fmt(amountPaid)}</span>
                            </button>
                            <div className="hero-stat-wrap__help">
                                <HeroKpiInfo
                                    helpText={t("dashboard.kpiHelp.paid")}
                                    ariaLabel={t("dashboard.kpiHelp.moreAbout", { topic: t("dashboard.paid") })}
                                />
                            </div>
                        </div>
                        <div className="hero-stat-wrap">
                            <button
                                type="button"
                                className="hero-stat hero-stat--danger hero-stat--clickable"
                                onClick={() => setPopup("overdue")}
                            >
                                <span className="hero-stat__label">{t("dashboard.overdue")}</span>
                                <span className="hero-stat__value">{fmt(amountOverdue)}</span>
                            </button>
                            <div className="hero-stat-wrap__help">
                                <HeroKpiInfo
                                    helpText={t("dashboard.kpiHelp.overdue")}
                                    ariaLabel={t("dashboard.kpiHelp.moreAbout", { topic: t("dashboard.overdue") })}
                                />
                            </div>
                        </div>
                    </div>
                    </div>
                    <button
                        type="button"
                        className="hero-card__pending hero-card__pending--inline"
                        onClick={() => navigate("/invoices", { state: { viewKey: "unsent" } })}
                    >
                        <span className="hero-card__pending-label">{t("dashboard.pendingToSend")}</span>
                        <span className="hero-card__pending-count">
                            {t("dashboard.pendingToSendCount", { count: pendingToSendCount })}
                        </span>
                        <span className="hero-card__pending-value">{fmt(amountPendingToSend)}</span>
                    </button>
                </div>
                <div className="hero-card__right">
                    <div className="hero-card__controls">
                        <div className="hero-card__actions">
                            <div className="hero-help-dropdown" ref={helpRef}>
                                <button
                                    type="button"
                                    className="hero-flow-btn hero-help-dropdown__trigger"
                                    onClick={() => setHelpOpen((v) => !v)}
                                    aria-expanded={helpOpen}
                                >
                                    <TbHelpCircle aria-hidden />
                                    {t("dashboard.help")}
                                    <TbChevronDown
                                        aria-hidden
                                        className={`hero-help-dropdown__arrow${helpOpen ? " hero-help-dropdown__arrow--open" : ""}`}
                                    />
                                </button>
                                {helpOpen && (
                                    <div className="hero-help-dropdown__menu">
                                        <button
                                            type="button"
                                            className="hero-help-dropdown__item"
                                            onClick={() => { setHelpOpen(false); onOpenFlowStoryboard(); }}
                                        >
                                            <TbBook2 aria-hidden /> {t("fileUpload.flowHowItWorks")}
                                        </button>
                                        <button
                                            type="button"
                                            className="hero-help-dropdown__item"
                                            onClick={() => { setHelpOpen(false); onOpenGettingStarted(); }}
                                        >
                                            <TbRocket aria-hidden /> {t("onboarding.gettingStartedTitle")}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button type="button" className="hero-flow-btn" onClick={onOpenStats}>
                                <TbChartBar aria-hidden />
                                {t("dashboard.statsButton")}
                            </button>
                            {canUpload && <span className="hero-card__actions-divider" aria-hidden />}
                            {canUpload && onCreateInvoice && (
                                <button type="button" className="hero-action-btn" onClick={onCreateInvoice}>
                                    <TbFilePlus aria-hidden />
                                    {t("invoiceCreate.entry")}
                                </button>
                            )}
                            {canUpload && (
                                <button type="button" className="hero-action-btn" onClick={onUpload}>
                                    <TbUpload aria-hidden />
                                    {t("fileUpload.uploadTitle")}
                                </button>
                            )}
                        </div>
                    </div>
                    {showOverviewSections && (
                    <div className={`hero-card__aside${pvsSavings != null ? " hero-card__aside--pvs" : ""}`}>
                        <section className="hero-card__section">
                            <h3 className="hero-card__section-title">{t("dashboard.overview")}</h3>
                            <div className="hero-card__mini-grid hero-card__mini-grid--overview">
                                <HeroMiniStat
                                    icon={<FaUserInjured />}
                                    label={t("dashboard.patients")}
                                    value={String(patientCount)}
                                    accent="#6366f1"
                                    onClick={() => navigate("/patients")}
                                />
                                <HeroMiniStat
                                    icon={<TbFileEuro />}
                                    label={t("dashboard.invoices")}
                                    value={String(totalInvoiceCount)}
                                    accent="#f59e0b"
                                    onClick={() => navigate("/invoices")}
                                />
                                <HeroMiniStat
                                    icon={<TbFileEuro />}
                                    label={t("dashboard.openInvoices")}
                                    value={String(openInvoiceCount)}
                                    accent="#ef4444"
                                    onClick={() => navigate("/invoices", { state: { viewKey: "open" } })}
                                />
                            </div>
                        </section>
                        <section className="hero-card__section">
                            <h3 className="hero-card__section-title">{t("dashboard.dunningStatus")}</h3>
                            <div className="hero-card__mini-grid hero-card__mini-grid--dunning">
                                {dunningLabels.map((label, i) => (
                                    <HeroMiniStat
                                        key={label}
                                        icon={<span className="hero-mini-stat__badge">{`M${i + 1}`}</span>}
                                        label={label}
                                        value={String(displayCounts[i])}
                                        sub={fmt(displayAmounts[i])}
                                        accent={DUNNING_COLORS[i]}
                                        onClick={() =>
                                            navigate("/invoices", {
                                                state: { viewKey: "open", filters: { dunning: String(DUNNING_LEVELS[i]) } },
                                            })
                                        }
                                    />
                                ))}
                            </div>
                        </section>
                    </div>
                )}
                </div>
                </div>
            </div>
            {(popup === "sent" || popup === "paid") && (
                <YearPopup
                    type={popup}
                    data={yearMonthlyData}
                    year={year}
                    onClose={() => setPopup(null)}
                />
            )}
            {popup === "overdue" && (
                <OverduePopup
                    breakdown={overdueBreakdown}
                    totalFines={overdueTotalFines}
                    total={amountOverdue}
                    onClose={() => setPopup(null)}
                />
            )}
        </>
    );
}
